import { useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useLocation } from 'react-router-dom';
import { API_URL } from '../constant/api-url';
import { SESSION_HEADER } from '../telemetry/http';
import { getSessionId, track } from '../telemetry';
import { isSessionError } from './useSessionGuard';

const API_ROOT = API_URL.endsWith('/') ? API_URL.slice(0, -1) : API_URL;

// "/api/ai/conversations/12/files/3/download", absolute or relative.
const DOWNLOAD_PATH =
  /\/api\/ai\/conversations\/(\d+)\/files\/(\d+)\/download\/?$/;

export const isApiDownloadLink = (href: string): boolean =>
  DOWNLOAD_PATH.test(href);

// The file name the API put in Content-Disposition. ASP.NET sends both forms; the RFC 5987
// `filename*` one is the faithful, percent-encoded one, so it is preferred and the only one decoded.
export const fileNameFromDisposition = (
  disposition: string | null,
  fallback: string,
): string => {
  if (!disposition) return fallback;
  const encoded = disposition.match(/filename\*=(?:UTF-8|utf-8)''([^;]+)/);
  if (encoded) {
    try {
      return decodeURIComponent(encoded[1].trim());
    } catch {
      // fall through to the plain form
    }
  }
  const plain = disposition.match(/filename="?([^";]+)"?/i);
  return plain ? plain[1].trim() : fallback;
};

export interface DownloadOutcome {
  ok: boolean;
  fileName: string;
}

// One authenticated fetch-and-save for every file the assistant produces. The download route
// needs the bearer token, so a plain link would be refused; this mirrors what the axios
// interceptors give ordinary requests: the session header, a sign-in redirect when Auth0 can no
// longer mint a token, and an api_error row on failure.
export default function useApiDownload() {
  const { getAccessTokenSilently, loginWithRedirect } = useAuth0();
  const { pathname, search } = useLocation();

  return useCallback(
    async (
      href: string,
      fallbackName = 'download',
    ): Promise<DownloadOutcome> => {
      const path = href.startsWith('http') ? new URL(href).pathname : href;
      const match = path.match(DOWNLOAD_PATH);
      const fileId = match ? Number(match[2]) : 0;
      const startedAt = performance.now();
      let fileName = fallbackName;
      let ok = false;
      let status = 0;

      try {
        let token: string;
        try {
          token = await getAccessTokenSilently();
        } catch (error) {
          if (isSessionError(error)) {
            loginWithRedirect({
              appState: { returnTo: `${pathname}${search}` },
            });
          }
          throw error;
        }

        const response = await fetch(`${API_ROOT}${path}`, {
          headers: {
            Authorization: `Bearer ${token}`,
            [SESSION_HEADER]: getSessionId(),
          },
        });
        ({ status } = response);
        if (!response.ok)
          throw new Error(`Download failed (${response.status})`);

        fileName = fileNameFromDisposition(
          response.headers.get('content-disposition'),
          fallbackName,
        );
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = fileName;
        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
        setTimeout(() => URL.revokeObjectURL(url), 10_000);
        ok = true;
      } catch {
        track('api_error', {
          endpoint: 'api/ai/conversations/{id}/files/{id}/download',
          method: 'GET',
          status,
          durationMs: Math.round(performance.now() - startedAt),
          kind: status ? 'http' : 'network',
        });
      } finally {
        track('ai_file_downloaded', {
          fileId,
          extension: fileName.split('.').pop()?.toLowerCase() ?? '',
          ok,
        });
      }

      return { ok, fileName };
    },
    [getAccessTokenSilently, loginWithRedirect, pathname, search],
  );
}
